import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import TsdiClient from 'src/models/AP/Client';
import FindClientDialog from 'src/components/client-project/dialogs/FindClientDialog';
import { useInfiniteScroll } from 'src/util/useInfiniteScroll';
import { useFillRemainingHeight } from 'src/util/useFillRemainingHeight';
import CostBreakdownSearch, { ICostBreakdownSearchCriteria } from '../components/CostBreakdownSearch';
import CostBreakdownQueryView from '../components/CostBreakdownQueryView';
import CostBreakdownJobsView from '../components/CostBreakdownJobsView';
import AddCostBreakdownReportDialog from '../dialogs/AddCostBreakdownReportDialog';
import CostBreakdownExcelDialog from '../dialogs/CostBreakdownExcelDialog';
import CostBreakdownJobDialog, { ISaveCostBreakdownJob } from '../dialogs/CostBreakdownJobDialog';
import ICostBreakdownReport from '../models/costBreakdownReport';
import ICostBreakdownProspectCandidate from '../models/costBreakdownProspectCandidate';
import ICostBreakdownJob from '../models/costBreakdownJob';
import PBNavBar from '../PBNavBar';

const formatDate = (value: string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

const CostBreakdownPage = () => {
    const dispatch = useDispatch();
    const { get, response } = useFetch();
    // Separate instances so these don't race/abort each other on the same use-http instance
    // (same caution CheckRegisterPage.tsx's own getOffices instance calls out).
    const { get: getClient, response: clientResponse } = useFetch();
    const { get: getProspects, response: prospectsResponse } = useFetch();
    const { post: createReport, response: createResponse } = useFetch();
    const { post: runQueryPost, response: runQueryResponse } = useFetch();
    const { get: getExcel, response: excelResponse } = useFetch();
    const { get: getJobClient, response: jobClientResponse } = useFetch();
    const { get: getJobProspects, response: jobProspectsResponse } = useFetch();
    const { get: getJobProspectsForEdit, response: jobProspectsForEditResponse } = useFetch();
    const { post: createJob, response: createJobResponse } = useFetch();
    const { put: updateJob, response: updateJobResponse } = useFetch();
    const [selectedRptId, setSelectedRptId] = useState<number | undefined>(undefined);
    // Set once Query succeeds - swaps the report list panel for the tabbed breakdown view, mirroring
    // TABCONTROL/CONTROL sharing one window in Oracle rather than opening a new page/dialog.
    const [queryRptId, setQueryRptId] = useState<number | undefined>(undefined);
    const [queryLoading, setQueryLoading] = useState<boolean>(false);
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(true);
    // The last-applied search filter - Add Report reuses its Beg/End Date rather than opening a
    // separate Dates entry popup like the Oracle source does, per explicit user direction.
    const [lastSearchCriteria, setLastSearchCriteria] = useState<ICostBreakdownSearchCriteria>({});
    const [addReportClient, setAddReportClient] = useState<TsdiClient | undefined>(undefined);
    const [addReportProspects, setAddReportProspects] = useState<Array<ICostBreakdownProspectCandidate>>([]);
    const [addReportSelected, setAddReportSelected] = useState<Record<number, boolean>>({});
    const reportListGrid = useFillRemainingHeight<HTMLDivElement>(32);
    // Swaps the report list/tabbed view for the Jobs master-detail screen, same "swap the panel"
    // idea as Query - mirrors the Jobs button implicitly showing the Jobs window in Oracle.
    const [showJobs, setShowJobs] = useState<boolean>(false);
    const [jobsRefreshKey, setJobsRefreshKey] = useState<number>(0);
    const [editingJob, setEditingJob] = useState<ICostBreakdownJob | undefined>(undefined);
    const [jobClient, setJobClient] = useState<TsdiClient | undefined>(undefined);
    const [jobProspects, setJobProspects] = useState<Array<ICostBreakdownProspectCandidate>>([]);
    const [jobSelected, setJobSelected] = useState<Record<number, boolean>>({});
    // Bumped on every Add/Edit Job open and used as the dialog's React key - forces a fresh mount
    // (and fresh useState init for the Weekday/Day-of-Month picker) every time, rather than only
    // when editingJob's identity happens to change between opens.
    const [jobDialogInstanceKey, setJobDialogInstanceKey] = useState<number>(0);
    // FindClientDialog is a single shared instance (one dialog active at a time) - this tracks which
    // flow (Add Report vs Add/Edit Job) should receive its result and get reopened afterward.
    const [findClientTarget, setFindClientTarget] = useState<'addReport' | 'job'>('addReport');

    // Builds the report list's OData query string for a given page from whatever search/sort is
    // currently active - read fresh from state each call (this closure is redefined every render,
    // not memoized), so it never goes stale the way a useCallback-frozen version could.
    const buildReportsQueryString = (pageNumber: number) => {
        const options = new ODataOptions().addPaging(pageNumber, 50);
        if (lastSearchCriteria.rptId !== undefined) {
            options.addFilter(new ODataFilter('rptId', ODataFilterOperators.Equals, lastSearchCriteria.rptId));
        }
        if (lastSearchCriteria.begDate !== undefined) {
            options.addFilter(new ODataFilter('begDate', ODataFilterOperators.Equals, lastSearchCriteria.begDate));
        }
        if (lastSearchCriteria.endDate !== undefined) {
            options.addFilter(new ODataFilter('endDate', ODataFilterOperators.Equals, lastSearchCriteria.endDate));
        }
        if (lastSearchCriteria.prospects !== undefined) {
            options.addFilter(new ODataFilter('prospects', ODataFilterOperators.Contains, lastSearchCriteria.prospects));
        }
        if (sortField) {
            options.addOrderBy(sortField, sortDescending ? ODataOrderByDirection.Descending : ODataOrderByDirection.Ascending);
        }
        return options.toString();
    };

    const reportsScroll = useInfiniteScroll<ICostBreakdownReport>(async (pageNumber) => {
        const result = await get(`/v1.0/CostBreakdown/reports${buildReportsQueryString(pageNumber)}`);
        if (!response.ok) {
            return { items: [], totalRecords: 0 };
        }
        return { items: result?.items ?? [], totalRecords: result?.totalRecords ?? 0 };
    }, 50);

    // Runs on mount and whenever search/sort changes - a fresh page 1 each time.
    useEffect(() => {
        reportsScroll.reset();
        reportsScroll.loadNextPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSearchCriteria, sortField, sortDescending]);

    // Keep the current selection if it's still present in the fresh results (e.g. a sort click),
    // otherwise default to the first row - so Query/Jobs/Excel aren't disabled on a fresh load/
    // search just because nothing's been clicked yet.
    useEffect(() => {
        setSelectedRptId((prev) => (prev !== undefined && reportsScroll.items.some((r) => r.rptId === prev)) ? prev : reportsScroll.items[0]?.rptId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportsScroll.items]);

    const handleSort = (field: string) => {
        const descending = field === sortField ? !sortDescending : false;
        setSortField(field);
        setSortDescending(descending);
    };

    const renderSortableHeader = (label: string, field: string, style?: React.CSSProperties) => {
        const willSortDescending = field === sortField ? !sortDescending : false;
        return (
            <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', ...style }}
                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                onClick={() => handleSort(field)}
            >
                {label}
                <Button
                    style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                    text={''}
                    reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                    iconHoverColor="blue"
                    iconStyle={{ width: '1.25rem', height: '1.25rem', verticalAlign: 'middle' }}
                />
            </th>
        );
    };

    const handleSearchQuery = (criteria: ICostBreakdownSearchCriteria) => {
        setLastSearchCriteria(criteria);
    };

    const handleSelectRow = (report: ICostBreakdownReport) => {
        setSelectedRptId(report.rptId === selectedRptId ? undefined : report.rptId);
    };

    // Mirrors B_QUERY (oid 32) - builds/refreshes cost_breakdown_temp for the selected report, then
    // shows the six-tab breakdown view in place of the report list. Build_Temp can genuinely run for
    // a while on a report with a lot of data (commandTimeout is 1800s server-side) - queryLoading
    // swaps the panel to a spinner for that stretch instead of leaving the screen looking frozen.
    const handleQuery = async () => {
        if (selectedRptId === undefined) {
            return;
        }
        setQueryLoading(true);
        await runQueryPost(`/v1.0/CostBreakdown/reports/${selectedRptId}/query`);
        setQueryLoading(false);
        if (runQueryResponse.ok) {
            setQueryRptId(selectedRptId);
        } else {
            window.alert('Unable to query the report. Please try again.');
        }
    };

    // Mirrors oid 38's Excel button -> the Excel Filename/Format popup -> CostBreakdown.rdl. Format
    // isn't a generic output-type picker - it's Oracle's :excel.format, selecting one of 3 separate
    // SSRS reports (Standard/Req-Landman/Budget-Margin). The popup's Browse button (a native Windows
    // Save As picker) has no web equivalent, so this just offers an editable suggested filename fed
    // to the browser's own download flow.
    const handleOpenExcelDialog = () => {
        if (selectedRptId === undefined) {
            return;
        }
        showDialog(DialogList.COST_BREAKDOWN_EXCEL);
    };

    const handleConfirmExcel = async (filename: string, reportPath: string) => {
        if (selectedRptId === undefined) {
            return;
        }
        await getExcel(`/v1.0/CostBreakdown/reports/${selectedRptId}/excel?reportPath=${encodeURIComponent(reportPath)}`);
        if (excelResponse.ok) {
            const blob = await excelResponse.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            hideDialog();
        } else {
            window.alert('Unable to generate the Excel report. Please try again.');
        }
    };

    const handleBackToList = () => {
        setQueryRptId(undefined);
    };

    const showDialog = (dialog: DialogList) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const handleOpenAddReport = () => {
        if (!lastSearchCriteria.begDate || !lastSearchCriteria.endDate) {
            window.alert('Please set Beginning Date and Ending Date in the search panel above, then click Search, before adding a report.');
            return;
        }
        setAddReportClient(undefined);
        setAddReportProspects([]);
        setAddReportSelected({});
        showDialog(DialogList.ADD_COST_BREAKDOWN_REPORT);
    };

    const fetchProspectsForClient = async (clientNumber: number) => {
        const result = await getProspects(`/v1.0/CostBreakdown/prospects/client/${clientNumber}`);
        if (prospectsResponse.ok) {
            setAddReportProspects(Array.isArray(result) ? result : []);
            setAddReportSelected({});
        }
    };

    // Mirrors WHEN-VALIDATE-ITEM (oid 337) - looks up the client's name and alerts on an invalid
    // number, matching Oracle's own "Invalid client number..." message.
    const lookupClientByNumber = async (clientNumber: number) => {
        const options = new ODataOptions().addPaging(1, 1);
        options.addFilter(new ODataFilter('ClientNumber', ODataFilterOperators.Equals, clientNumber));
        const result = await getClient(`/v1.0/AccountsPayable/clients${options.toString()}`);
        if (clientResponse.ok && Array.isArray(result?.items) && result.items.length > 0) {
            setAddReportClient(result.items[0]);
        } else {
            setAddReportClient(undefined);
            window.alert('Invalid client number...');
        }
    };

    // Mirrors KEY-NEXT-ITEM (oid 338) - entering/tabbing off a Client No. auto-queries that
    // client's projects.
    const handleClientNumberBlur = (value: string) => {
        const clientNumber = Number(value);
        if (!value || isNaN(clientNumber)) {
            return;
        }
        lookupClientByNumber(clientNumber);
        fetchProspectsForClient(clientNumber);
    };

    const handleFindClient = () => {
        setFindClientTarget('addReport');
        showDialog(DialogList.FIND_CLIENT);
    };

    const handleFindClientForJob = () => {
        setFindClientTarget('job');
        showDialog(DialogList.FIND_CLIENT);
    };

    const handleClientSelectedFromFind = (client: TsdiClient) => {
        // FindClientDialog calls whatever's passed as hideDialog right after selecting - only one
        // dialog can be active at a time, so reopening the originating dialog has to happen on the
        // next tick or this call clears it right back out (see feedback_dialog_launch_pattern).
        if (findClientTarget === 'job') {
            setJobClient(client);
            fetchProspectsForJobClient(client.clientNumber);
            setTimeout(() => showDialog(DialogList.COST_BREAKDOWN_JOB), 0);
        } else {
            setAddReportClient(client);
            fetchProspectsForClient(client.clientNumber);
            setTimeout(() => showDialog(DialogList.ADD_COST_BREAKDOWN_REPORT), 0);
        }
    };

    const closeFindClientAndReturnToOrigin = () => {
        hideDialog();
        setTimeout(() => showDialog(findClientTarget === 'job' ? DialogList.COST_BREAKDOWN_JOB : DialogList.ADD_COST_BREAKDOWN_REPORT), 0);
    };

    const handleToggleProspect = (prId: number) => {
        setAddReportSelected((prev) => ({ ...prev, [prId]: !prev[prId] }));
    };

    const handleSelectAllProspects = () => {
        const all: Record<number, boolean> = {};
        addReportProspects.forEach((p) => { all[p.prId] = true; });
        setAddReportSelected(all);
    };

    const handleDeselectAllProspects = () => {
        setAddReportSelected({});
    };

    // Mirrors oid 357 (No Admin) / oid 360 (W/Admin) - same validation and insert shape, only
    // IncludeAdmin differs.
    const handleCreateReport = async (begDate: string, endDate: string, includeAdmin: boolean) => {
        if (!begDate) {
            window.alert('A beginning date must be entered before a report can be created...');
            return;
        }
        if (!endDate) {
            window.alert('An ending date must be entered before a report can be created...');
            return;
        }
        const prospectIds = Object.keys(addReportSelected).filter((k) => addReportSelected[Number(k)]).map(Number);
        if (prospectIds.length === 0) {
            window.alert('At least one prospect must be selected before a report can be created...');
            return;
        }

        const newRptId = await createReport('/v1.0/CostBreakdown/reports', { begDate, endDate, includeAdmin, prospectIds });
        if (createResponse.ok) {
            hideDialog();
            setSelectedRptId(newRptId);
            reportsScroll.reset();
            reportsScroll.loadNextPage();
        } else {
            window.alert('Unable to create the report. Please try again.');
        }
    };

    // Mirrors clicking Jobs (oid 35's Query_Block('Cost_Breakdown_Job'), which implicitly shows the
    // Jobs window in Oracle) - independent of any selected report row, so unlike Query/Excel this
    // isn't gated on selectedRptId.
    const handleOpenJobs = () => {
        setShowJobs(true);
    };

    const handleBackFromJobs = () => {
        setShowJobs(false);
    };

    const fetchProspectsForJobClient = async (clientNumber: number) => {
        const result = await getJobProspects(`/v1.0/CostBreakdown/prospects/client/${clientNumber}`);
        if (jobProspectsResponse.ok) {
            setJobProspects(Array.isArray(result) ? result : []);
        }
    };

    const lookupJobClientByNumber = async (clientNumber: number) => {
        const options = new ODataOptions().addPaging(1, 1);
        options.addFilter(new ODataFilter('ClientNumber', ODataFilterOperators.Equals, clientNumber));
        const result = await getJobClient(`/v1.0/AccountsPayable/clients${options.toString()}`);
        if (jobClientResponse.ok && Array.isArray(result?.items) && result.items.length > 0) {
            setJobClient(result.items[0]);
        } else {
            setJobClient(undefined);
            window.alert('Invalid client number...');
        }
    };

    const handleJobClientNumberBlur = (value: string) => {
        const clientNumber = Number(value);
        if (!value || isNaN(clientNumber)) {
            return;
        }
        lookupJobClientByNumber(clientNumber);
        fetchProspectsForJobClient(clientNumber);
        setJobSelected({});
    };

    const handleOpenAddJob = () => {
        setEditingJob(undefined);
        setJobClient(undefined);
        setJobProspects([]);
        setJobSelected({});
        setJobDialogInstanceKey((k) => k + 1);
        showDialog(DialogList.COST_BREAKDOWN_JOB);
    };

    // Mirrors EDIT_JOB's own setup - loads the job's client/prospects like Add Job, but pre-selects
    // whichever prospects are already attached to this job (COST_BREAKDOWN_JOB_PROSPECT).
    const handleOpenEditJob = async (job: ICostBreakdownJob) => {
        setEditingJob(job);
        setJobSelected({});

        const clientOptions = new ODataOptions().addPaging(1, 1);
        clientOptions.addFilter(new ODataFilter('ClientNumber', ODataFilterOperators.Equals, job.clNo));

        const [clientResult, prospectsResult, existingResult] = await Promise.all([
            getJobClient(`/v1.0/AccountsPayable/clients${clientOptions.toString()}`),
            getJobProspects(`/v1.0/CostBreakdown/prospects/client/${job.clNo}`),
            getJobProspectsForEdit(`/v1.0/CostBreakdown/jobs/${job.id}/prospects`),
        ]);
        setJobClient(jobClientResponse.ok && Array.isArray(clientResult?.items) && clientResult.items.length > 0 ? clientResult.items[0] : undefined);
        if (jobProspectsResponse.ok) {
            setJobProspects(Array.isArray(prospectsResult) ? prospectsResult : []);
        }
        if (jobProspectsForEditResponse.ok && Array.isArray(existingResult)) {
            const preselected: Record<number, boolean> = {};
            existingResult.forEach((p: { prId: number }) => { preselected[p.prId] = true; });
            setJobSelected(preselected);
        }
        setJobDialogInstanceKey((k) => k + 1);
        showDialog(DialogList.COST_BREAKDOWN_JOB);
    };

    const handleToggleJobProspect = (prId: number) => {
        setJobSelected((prev) => ({ ...prev, [prId]: !prev[prId] }));
    };

    const handleSelectAllJobProspects = () => {
        const all: Record<number, boolean> = {};
        jobProspects.forEach((p) => { all[p.prId] = true; });
        setJobSelected(all);
    };

    const handleDeselectAllJobProspects = () => {
        setJobSelected({});
    };

    // Mirrors ADD_JOB/EDIT_JOB - same validation/save shape, only whether a PUT or POST fires differs.
    const handleSaveJob = async (data: ISaveCostBreakdownJob) => {
        if (!data.clNo) {
            window.alert('A client number must be defined before a job can be created...');
            return;
        }
        if (!data.email) {
            window.alert('An email address must be defined before a job can be created...');
            return;
        }
        const prospectIds = Object.keys(jobSelected).filter((k) => jobSelected[Number(k)]).map(Number);
        if (prospectIds.length === 0) {
            window.alert('At least one prospect must be selected before a job can be created...');
            return;
        }

        const request = { ...data, prospectIds };
        if (editingJob) {
            await updateJob(`/v1.0/CostBreakdown/jobs/${editingJob.id}`, request);
            if (!updateJobResponse.ok) {
                window.alert('Unable to update the job. Please try again.');
                return;
            }
        } else {
            await createJob('/v1.0/CostBreakdown/jobs', request);
            if (!createJobResponse.ok) {
                window.alert('Unable to create the job. Please try again.');
                return;
            }
        }
        hideDialog();
        setJobsRefreshKey((k) => k + 1);
    };

    const buttonWidth = { whiteSpace: 'nowrap' as const, height: '3rem', marginLeft: '.5rem' };

    return (
        <div>
            <PBNavBar />
            <CostBreakdownSearch runQuery={handleSearchQuery} />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ marginBottom: '.5rem', alignItems: 'center', justifyContent: 'center' }}>
                                <Button className="blue-hover-button" style={buttonWidth} text="Add Report" disabled={queryLoading} onClick={() => handleOpenAddReport()} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Detail View" disabled={selectedRptId === undefined || queryLoading} onClick={() => handleQuery()} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Jobs" disabled={queryLoading} onClick={() => handleOpenJobs()} />
                                <Button
                                    className="blue-hover-button"
                                    style={buttonWidth}
                                    text="Report"
                                    disabled={selectedRptId === undefined || queryLoading}
                                    onClick={() => handleOpenExcelDialog()}
                                />
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel
                            panelTitle={
                                showJobs
                                    ? 'Cost Breakdown Jobs'
                                    : queryLoading
                                        ? `Building Report #${selectedRptId}...`
                                        : queryRptId === undefined
                                            ? 'Cost Breakdown Reports'
                                            : `Cost Breakdown Report #${queryRptId}`
                            }
                        >
                            {showJobs ? (
                                <CostBreakdownJobsView
                                    onBack={handleBackFromJobs}
                                    onAddJob={handleOpenAddJob}
                                    onEditJob={handleOpenEditJob}
                                    refreshKey={jobsRefreshKey}
                                />
                            ) : queryLoading ? (
                                <div style={{ textAlign: 'center', padding: '4rem 0' }}>
                                    <LoadingSpinner />
                                </div>
                            ) : queryRptId === undefined ? (
                                <div ref={reportListGrid.ref} style={{ overflowY: 'scroll', height: reportListGrid.height }} onScroll={reportsScroll.onScroll}>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                {renderSortableHeader('Report Id', 'rptId', { width: '10%' })}
                                                {renderSortableHeader('Beg. Date', 'begDate', { width: '12%' })}
                                                {renderSortableHeader('End Date', 'endDate', { width: '12%' })}
                                                <th style={{ width: '28%' }}>Prospects</th>
                                                {renderSortableHeader('Created By', 'createBy', { width: '13%' })}
                                                {renderSortableHeader('Created On', 'createDate', { width: '12%' })}
                                                {renderSortableHeader('Refreshed', 'refreshDate', { width: '13%' })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportsScroll.loading && reportsScroll.items.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} className="bold-centered">
                                                        <LoadingSpinner />
                                                    </td>
                                                </tr>
                                            )}
                                            {reportsScroll.items.map((report) => (
                                                <tr
                                                    key={report.rptId}
                                                    className="table-item-hover"
                                                    style={{
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        backgroundColor: report.rptId === selectedRptId ? '#FFFF66' : '',
                                                    }}
                                                    onClick={() => handleSelectRow(report)}
                                                >
                                                    <td>{report.rptId}</td>
                                                    <td>{formatDate(report.begDate)}</td>
                                                    <td>{formatDate(report.endDate)}</td>
                                                    <td style={{ textAlign: 'left' }}>{report.prospects}</td>
                                                    <td>{report.createBy}</td>
                                                    <td>{formatDate(report.createDate)}</td>
                                                    <td>{formatDate(report.refreshDate)}</td>
                                                </tr>
                                            ))}
                                            {!reportsScroll.loading && reportsScroll.items.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} className="bold-centered">No Reports Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <CostBreakdownQueryView rptId={queryRptId} onBack={handleBackToList} />
                            )}
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <AddCostBreakdownReportDialog
                dialogName={DialogList.ADD_COST_BREAKDOWN_REPORT}
                client={addReportClient}
                initialClientNumber={addReportClient?.clientNumber}
                initialBegDate={lastSearchCriteria.begDate ?? ''}
                initialEndDate={lastSearchCriteria.endDate ?? ''}
                onClientNumberBlur={handleClientNumberBlur}
                onFindClient={handleFindClient}
                prospects={addReportProspects}
                selected={addReportSelected}
                onToggleProspect={handleToggleProspect}
                onSelectAll={handleSelectAllProspects}
                onDeselectAll={handleDeselectAllProspects}
                onCancel={hideDialog}
                onCreate={handleCreateReport}
            />
            <FindClientDialog
                dialogName={DialogList.FIND_CLIENT}
                hideDialog={closeFindClientAndReturnToOrigin}
                setSelectedClient={handleClientSelectedFromFind}
            />
            <CostBreakdownExcelDialog
                dialogName={DialogList.COST_BREAKDOWN_EXCEL}
                initialFilename={`CostBreakdown_${selectedRptId}.xlsx`}
                onCancel={hideDialog}
                onConfirm={handleConfirmExcel}
            />
            <CostBreakdownJobDialog
                // Forces a fresh mount (and fresh useState init) on every open - otherwise the
                // Weekday/Day-of-Month picker's local state carries over from whichever job was
                // last opened instead of reflecting the newly-selected one.
                key={jobDialogInstanceKey}
                dialogName={DialogList.COST_BREAKDOWN_JOB}
                editingJob={editingJob}
                client={jobClient}
                initialClientNumber={jobClient?.clientNumber ?? editingJob?.clNo}
                onClientNumberBlur={handleJobClientNumberBlur}
                onFindClient={handleFindClientForJob}
                prospects={jobProspects}
                selected={jobSelected}
                onToggleProspect={handleToggleJobProspect}
                onSelectAll={handleSelectAllJobProspects}
                onDeselectAll={handleDeselectAllJobProspects}
                onCancel={hideDialog}
                onSave={handleSaveJob}
            />
        </div>
    );
};

export default CostBreakdownPage;



// WEBPACK FOOTER //
// ./src/components/project-billing/pages/CostBreakdownPage.tsx